import {
  BODY_SLOTS,
  cardAttributes,
  cardSynergyTypes,
  cardCostTotal,
  resolveCardBoardSlot,
} from "./deckSuggest";

const KIND_TYPES = new Set(["equipment", "ability", "aspect"]);
const RESOURCE_KEYS = ["strength", "dexterity", "wisdom", "neutral"];
const EQUIP_TAG_SLOTS = [
  "oneHandedWeapon",
  "twoHandedWeapon",
  "offHand",
  "head",
  "hands",
  "feet",
  "body",
  "trinket",
];

const BOARD_SLOT_LABELS = {
  weapon: "Weapon",
  armor: "Armor",
  trinket: "Trinket",
};

const EQUIP_SLOT_LABELS = {
  oneHandedWeapon: "One-Handed",
  twoHandedWeapon: "Two-Handed",
  offHand: "Off-Hand",
  head: "Head",
  hands: "Hands",
  feet: "Feet",
  body: "Body",
  trinket: "Trinket",
};

const KIND_LABELS = {
  active: "Active",
  augment: "Augment",
  essence: "Essence",
};

function emptyResources() {
  return { strength: 0, dexterity: 0, wisdom: 0, neutral: 0 };
}

function emptyOutputs() {
  return {
    damageByType: {},
    heal: 0,
    regeneration: 0,
    physicalArmor: 0,
    magicArmor: 0,
    resources: emptyResources(),
    resourceTotal: 0,
    draw: 0,
    ailmentByType: {},
  };
}

function bump(map, key, amount = 1) {
  if (!key || !(amount > 0)) return;
  map[key] = (map[key] || 0) + amount;
}

function addResources(target, source, scale = 1) {
  RESOURCE_KEYS.forEach((key) => {
    const amount = (Number(source?.[key]) || 0) * scale;
    if (amount) target[key] = (target[key] || 0) + amount;
  });
}

function resourceTotal(resources) {
  return RESOURCE_KEYS.reduce(
    (sum, key) => sum + (Number(resources?.[key]) || 0),
    0,
  );
}

function scaleOutputs(outputs, scale) {
  if (!outputs || scale === 1) return outputs || emptyOutputs();
  const next = emptyOutputs();
  Object.entries(outputs.damageByType || {}).forEach(([type, amount]) => {
    bump(next.damageByType, type, amount * scale);
  });
  Object.entries(outputs.ailmentByType || {}).forEach(([type, amount]) => {
    bump(next.ailmentByType, type, amount * scale);
  });
  next.heal = (outputs.heal || 0) * scale;
  next.regeneration = (outputs.regeneration || 0) * scale;
  next.physicalArmor = (outputs.physicalArmor || 0) * scale;
  next.magicArmor = (outputs.magicArmor || 0) * scale;
  next.draw = (outputs.draw || 0) * scale;
  addResources(next.resources, outputs.resources, scale);
  next.resourceTotal = resourceTotal(next.resources);
  return next;
}

function mergeOutputs(target, source, scale = 1) {
  if (!source) return target;
  const scaled = scale === 1 ? source : scaleOutputs(source, scale);
  Object.entries(scaled.damageByType || {}).forEach(([type, amount]) => {
    bump(target.damageByType, type, amount);
  });
  Object.entries(scaled.ailmentByType || {}).forEach(([type, amount]) => {
    bump(target.ailmentByType, type, amount);
  });
  target.heal += scaled.heal || 0;
  target.regeneration += scaled.regeneration || 0;
  target.physicalArmor += scaled.physicalArmor || 0;
  target.magicArmor += scaled.magicArmor || 0;
  target.draw += scaled.draw || 0;
  addResources(target.resources, scaled.resources);
  target.resourceTotal = resourceTotal(target.resources);
  return target;
}

function averageOutputs(list) {
  const items = (list || []).filter(Boolean);
  if (!items.length) return emptyOutputs();
  const total = emptyOutputs();
  items.forEach((item) => mergeOutputs(total, item));
  return scaleOutputs(total, 1 / items.length);
}

function sumOutputs(list) {
  const total = emptyOutputs();
  (list || []).forEach((item) => mergeOutputs(total, item));
  return total;
}

function leafEffectOutputs(entry) {
  const out = emptyOutputs();
  if (!entry || typeof entry !== "object") return out;

  if (entry.id === "damageOpponent" || entry.id === "attackFromIdentity") {
    const amount = Math.max(0, Number(entry.amount) || 0);
    const type = entry.damageType || "physical";
    if (amount > 0) bump(out.damageByType, type, amount);
    return out;
  }
  if (
    entry.id === "convertPhysicalArmorToDamage" ||
    entry.id === "convertMagicArmorToDamage"
  ) {
    const amount = Math.max(0, Number(entry.amount) || 0);
    const type = entry.damageType || "physical";
    if (amount > 0) bump(out.damageByType, type, amount);
    return out;
  }
  if (entry.id === "healSelf") {
    out.heal = Math.max(0, Number(entry.amount) || 0);
    return out;
  }
  if (entry.id === "regeneration") {
    out.regeneration = Math.max(0, Number(entry.amount) || 0);
    return out;
  }
  if (
    entry.id === "physicalArmor" ||
    entry.id === "temporaryPhysicalArmor"
  ) {
    out.physicalArmor = Math.max(0, Number(entry.amount) || 0);
    return out;
  }
  if (entry.id === "magicArmor" || entry.id === "temporaryMagicArmor") {
    out.magicArmor = Math.max(0, Number(entry.amount) || 0);
    return out;
  }
  if (entry.id === "temporaryArmorFromIdentity") {
    out.physicalArmor = Math.max(0, Number(entry.amount) || 0) * 0.5;
    out.magicArmor = Math.max(0, Number(entry.amount) || 0) * 0.5;
    return out;
  }
  if (
    entry.id === "grantResources" ||
    entry.id === "gainResources" ||
    entry.id === "persist"
  ) {
    if (entry.resources) {
      addResources(out.resources, entry.resources);
    } else if (entry.attribute) {
      bump(out.resources, entry.attribute, Math.max(0, Number(entry.amount) || 1));
    }
    out.resourceTotal = resourceTotal(out.resources);
    return out;
  }
  if (
    entry.id === "draw" ||
    entry.id === "drawEquipment" ||
    entry.id === "drawAbility" ||
    entry.id === "discardSelfThenDraw" ||
    entry.id === "discardHandThenDraw"
  ) {
    out.draw = Math.max(1, Number(entry.amount) || 1);
    return out;
  }
  if (
    entry.id === "burn" ||
    entry.id === "chill" ||
    entry.id === "shock" ||
    entry.id === "wither" ||
    entry.id === "bleed" ||
    entry.id === "poison"
  ) {
    bump(out.ailmentByType, entry.id, Math.max(0, Number(entry.amount) || 0));
    return out;
  }
  if (entry.id === "applyAilment" || entry.id === "ailmentFromIdentity") {
    const ailment = entry.ailment || entry.type || "ailment";
    bump(out.ailmentByType, ailment, Math.max(0, Number(entry.amount) || 0));
    return out;
  }
  if (entry.id === "identityExpend" && entry.byIdentity) {
    const variants = Object.values(entry.byIdentity).map((value) =>
      analyzeEffect(value),
    );
    return averageOutputs(variants);
  }
  if (entry.id === "chooseIdentity" && entry.byIdentity) {
    const variants = Object.values(entry.byIdentity).map((value) =>
      analyzeEffect(value),
    );
    return averageOutputs(variants);
  }
  return out;
}

function analyzeEffect(effect) {
  if (!effect) return emptyOutputs();
  if (Array.isArray(effect.choose)) {
    return averageOutputs(effect.choose.map((entry) => analyzeEffect(entry)));
  }
  if (Array.isArray(effect)) {
    return sumOutputs(effect.map((entry) => analyzeEffect(entry)));
  }
  if (effect.byIdentity && typeof effect.byIdentity === "object") {
    return averageOutputs(
      Object.values(effect.byIdentity).map((entry) => analyzeEffect(entry)),
    );
  }
  return leafEffectOutputs(effect);
}

function resolveCards(ids, getCard) {
  return (ids || [])
    .map((id) => (typeof id === "string" ? getCard(id) : id))
    .filter(Boolean);
}

function countMapToRows(map, labelFn) {
  return Object.entries(map || {})
    .filter(([, count]) => count > 0)
    .sort((a, b) => {
      if (b[1] !== a[1]) return b[1] - a[1];
      return String(a[0]).localeCompare(String(b[0]));
    })
    .map(([key, count]) => ({
      key,
      label: labelFn ? labelFn(key) : key,
      count,
    }));
}

function averagesFromTotals(totals, count) {
  if (!(count > 0)) {
    return {
      damageByType: {},
      heal: 0,
      regeneration: 0,
      physicalArmor: 0,
      magicArmor: 0,
      resources: emptyResources(),
      resourceTotal: 0,
      draw: 0,
      ailmentByType: {},
      cost: 0,
    };
  }
  const scaled = scaleOutputs(totals, 1 / count);
  return {
    ...scaled,
    cost: (totals.cost || 0) / count,
  };
}

function presentMetricRows(averages, totals) {
  const rows = [];
  Object.entries(averages.damageByType || {}).forEach(([type, avg]) => {
    if (!(avg > 0)) return;
    rows.push({
      key: `damage:${type}`,
      label: `${formatTitle(type)} damage`,
      average: avg,
      total: totals.damageByType?.[type] || 0,
      kind: "damage",
    });
  });
  if ((averages.heal || 0) > 0) {
    rows.push({
      key: "heal",
      label: "Heal",
      average: averages.heal,
      total: totals.heal || 0,
      kind: "heal",
    });
  }
  if ((averages.regeneration || 0) > 0) {
    rows.push({
      key: "regeneration",
      label: "Regeneration",
      average: averages.regeneration,
      total: totals.regeneration || 0,
      kind: "heal",
    });
  }
  if ((averages.physicalArmor || 0) > 0) {
    rows.push({
      key: "physicalArmor",
      label: "Physical armor",
      average: averages.physicalArmor,
      total: totals.physicalArmor || 0,
      kind: "armor",
    });
  }
  if ((averages.magicArmor || 0) > 0) {
    rows.push({
      key: "magicArmor",
      label: "Magic armor",
      average: averages.magicArmor,
      total: totals.magicArmor || 0,
      kind: "armor",
    });
  }
  Object.entries(averages.ailmentByType || {}).forEach(([type, avg]) => {
    if (!(avg > 0)) return;
    rows.push({
      key: `ailment:${type}`,
      label: `${formatTitle(type)} stacks`,
      average: avg,
      total: totals.ailmentByType?.[type] || 0,
      kind: "ailment",
    });
  });
  if ((averages.draw || 0) > 0) {
    rows.push({
      key: "draw",
      label: "Draw",
      average: averages.draw,
      total: totals.draw || 0,
      kind: "draw",
    });
  }
  RESOURCE_KEYS.forEach((key) => {
    const avg = averages.resources?.[key] || 0;
    if (!(avg > 0)) return;
    rows.push({
      key: `resource:${key}`,
      label: `${formatTitle(key)} resources`,
      average: avg,
      total: totals.resources?.[key] || 0,
      kind: "resource",
    });
  });
  return rows.sort((a, b) => {
    if (b.average !== a.average) return b.average - a.average;
    return a.label.localeCompare(b.label);
  });
}

function formatTitle(value) {
  if (!value) return "";
  return String(value).charAt(0).toUpperCase() + String(value).slice(1);
}

function analyzeCardList(cards, { includeEquipSlots = false } = {}) {
  const typeCounts = {};
  const attributeCounts = {};
  const boardSlotCounts = {};
  const equipSlotCounts = {};
  const kindCounts = {};
  const totals = emptyOutputs();
  totals.cost = 0;
  let power = 0;

  cards.forEach((card) => {
    power += Math.max(0, Number(card.power) || 0);
    cardSynergyTypes(card).forEach((type) => bump(typeCounts, type));
    cardAttributes(card).forEach((attribute) =>
      bump(attributeCounts, attribute),
    );

    const boardSlot = resolveCardBoardSlot(card);
    if (boardSlot) bump(boardSlotCounts, boardSlot);
    if (includeEquipSlots && card.slot) bump(equipSlotCounts, card.slot);
    if (card.kind) bump(kindCounts, card.kind);

    totals.cost += cardCostTotal(card);

    if (card.resources) {
      const grant = emptyOutputs();
      addResources(grant.resources, card.resources);
      grant.resourceTotal = resourceTotal(grant.resources);
      mergeOutputs(totals, grant);
    }

    mergeOutputs(totals, analyzeEffect(card.activatedEffect));
    mergeOutputs(totals, analyzeEffect(card.effect));
    mergeOutputs(totals, analyzeEffect(card.invoke));
    if (card.kind === "active" && card.combo) {
      mergeOutputs(totals, scaleOutputs(analyzeEffect(card.combo), 0.5));
    }
  });

  const count = cards.length;
  const averages = averagesFromTotals(totals, count);
  return {
    count,
    power,
    types: countMapToRows(typeCounts, formatTitle),
    attributes: countMapToRows(attributeCounts, formatTitle),
    boardSlots: BODY_SLOTS.filter((slot) => (boardSlotCounts[slot] || 0) > 0).map(
      (slot) => ({
        key: slot,
        label: BOARD_SLOT_LABELS[slot] || formatTitle(slot),
        count: boardSlotCounts[slot],
      }),
    ),
    equipSlots: EQUIP_TAG_SLOTS.filter(
      (slot) => (equipSlotCounts[slot] || 0) > 0,
    ).map((slot) => ({
      key: slot,
      label: EQUIP_SLOT_LABELS[slot] || formatTitle(slot),
      count: equipSlotCounts[slot],
    })),
    kinds: ["active", "augment", "essence"]
      .filter((kind) => (kindCounts[kind] || 0) > 0)
      .map((kind) => ({
        key: kind,
        label: KIND_LABELS[kind] || formatTitle(kind),
        count: kindCounts[kind],
      })),
    averages,
    totals,
    outputRows: presentMetricRows(averages, totals),
  };
}

function analyzeAspect(card) {
  if (!card) {
    return {
      present: false,
      name: null,
      power: 0,
      attributes: [],
      types: [],
      text: null,
    };
  }
  const types = (Array.isArray(card.type) ? card.type : card.type ? [card.type] : [])
    .filter((type) => type && !KIND_TYPES.has(type));
  return {
    present: true,
    name: card.name || card.id,
    power: Math.max(0, Number(card.power) || 0),
    attributes: cardAttributes(card).map((attribute) => ({
      key: attribute,
      label: formatTitle(attribute),
      count: 1,
    })),
    types: types.map((type) => ({
      key: type,
      label: formatTitle(type),
      count: 1,
    })),
    text: card.text || null,
  };
}

function analyzeDeck(equipmentIds, abilityIds, aspectId, getCard) {
  const equipmentCards = resolveCards(equipmentIds, getCard);
  const abilityCards = resolveCards(abilityIds, getCard);
  const aspectCard = aspectId ? getCard(aspectId) : null;
  const equipment = analyzeCardList(equipmentCards, { includeEquipSlots: true });
  const ability = analyzeCardList(abilityCards);
  const aspect = analyzeAspect(aspectCard);

  const combinedTypeCounts = {};
  [...equipment.types, ...ability.types, ...aspect.types].forEach((row) => {
    bump(combinedTypeCounts, row.key, row.count);
  });
  const combinedAttributeCounts = {};
  [...equipment.attributes, ...ability.attributes, ...aspect.attributes].forEach(
    (row) => {
      bump(combinedAttributeCounts, row.key, row.count);
    },
  );

  return {
    aspect,
    equipment,
    ability,
    overview: {
      cardCount: equipment.count + ability.count + (aspect.present ? 1 : 0),
      power: equipment.power + ability.power + aspect.power,
      types: countMapToRows(combinedTypeCounts, formatTitle),
      attributes: countMapToRows(combinedAttributeCounts, formatTitle),
    },
  };
}

function formatAverage(value) {
  const amount = Number(value) || 0;
  if (!amount) return "0";
  if (Number.isInteger(amount)) return String(amount);
  return amount.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
}

export {
  BODY_SLOTS,
  BOARD_SLOT_LABELS,
  EQUIP_SLOT_LABELS,
  KIND_LABELS,
  analyzeDeck,
  analyzeEffect,
  analyzeCardList,
  formatAverage,
  formatTitle,
};
